import {EventEmitter, Injectable} from '@angular/core';
import {ComponentType} from '@angular/cdk/portal';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute} from '@angular/router';
import {Observable, of, ReplaySubject, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import * as moment from 'moment';

import {
	HttpStatusCodesEnum,
	FundProductsService,
	FundProductInterface,
	FundOrderTypesEnum,
	FundProductTypesEnum,
	FundProductOrderPricesInterface,
	FundProductPricesService,
	FundInvestmentRowInterface,
	ProductSubType, AccountBalanceResponseInterface,
} from '@spa/api';

import {AlertsService, Client} from 'src/app/shared/services';
import {AlertTypesEnum} from 'src/app/shared/enums';
import {Helper} from 'src/app/shared/classes';
import {LanguageService} from 'src/app/shared/language.service';
import {RadioGroupOptionInterface} from 'src/app/shared/components/radio-group/interfaces';
import {IPaginationConfig, Pagination} from 'src/app/shared/components';
import {GetProductFilterControls, GetProductTableColumns, InitProductFilterOptions} from './products.functions';
import {ProductHelper} from './products.helper';
import {
	Product,
	IProductControls,
	ProductFilter,
	IGroupedProducts,
	ProductDialogType,
	IProductTableColumns
} from './products.models';

@Injectable()
export class ProductsService {
	private readonly searchCache: { [key: string]: { match: boolean } };
	public columns: IProductTableColumns;
	public filter: ProductFilter;
	public filterToggle: Subject<{ isPopup: boolean }>;
	public product: ReplaySubject<Product>;
	public products: Product[];
	public groupedProducts: IGroupedProducts;
	public controls: IProductControls;

	constructor(
		private dialog: MatDialog,
		private readonly fundProductPricesService: FundProductPricesService,
		private readonly languageService: LanguageService,
		private readonly alertsService: AlertsService,
		private readonly fundProductsService: FundProductsService,
	) {
		this.searchCache = {};
		this.filterToggle = new Subject();
		this.filter = new ProductFilter();
		this.product = new ReplaySubject();
		this.groupedProducts = {
			subType: 0,
			items: [],
		};
	}

	private initTableColumns(isPreview: boolean): void {
		const subType = this.filter.subType && this.filter.subType.length > 0
			? this.filter.subType[0]
			: ProductSubType.NotAvailable;

		const type = this.filter.type && this.filter.type.length > 0
			? this.filter.type[0]
			: null;

		this.columns = GetProductTableColumns(isPreview, subType, type);
	}

	private applyKeywordFilter(product: Product): void {
		if (product.hidden) {
			return;
		}

		const search = this.filter.keyword || '';

		if (search.length > 1) {
			const searchLower = search.toLowerCase();

			const key = `${searchLower}|${product.id}`;

			const cache = this.searchCache[key];

			if (cache) {
				if (!cache.match) {
					product.hidden = true;
				}

				return;
			}

			const pattern = searchLower.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

			const regex = new RegExp('.*' + pattern + '.*');

			const searchFields = [
				...product.underlyingInstruments.map(item => item.name),
				...product.underlyingInstruments.map(item => item.symbol),
				product.version,
				product.name,
				product.nameBG,
				product.code,
			];

			const match = searchFields.find(field => {
				return field && regex.test(field.toLowerCase());
			});

			if (match) {
				this.searchCache[key] = {match: true};
			} else {
				product.hidden = true;
				this.searchCache[key] = {match: false};
			}
		}
	}

	private applyFilters(): void {
		const filter = this.filter;

		this.products.forEach(product => {
			product.hidden = false;

			if (filter.status && filter.status.indexOf(product.status) === -1) {
				product.hidden = true;
			} else if (filter.issuedBy && filter.issuedBy.indexOf(product.issuedBy) === -1) {
				product.hidden = true;
			} else if (filter.category && filter.category.indexOf(product.category) === -1) {
				product.hidden = true;
			} else if (filter.group && filter.group.indexOf(product.group) === -1) {
				product.hidden = true;
			} else if (filter.subType && filter.subType.indexOf(product.subType) === -1) {
				product.hidden = true;
			} else if (filter.type && filter.type.indexOf(product.type) === -1) {
				product.hidden = true;
			}

			if (!product.hidden && filter.annualizedYieldToMaturity) {
				product.hidden = filter.annualizedYieldToMaturity.map(option => {
					switch (option) {
						case 0:
							return product.annualizedYieldToMaturity < 2;
						case 1:
							return product.annualizedYieldToMaturity >= 2 && product.annualizedYieldToMaturity < 3;
						case 2:
							return product.annualizedYieldToMaturity >= 3 && product.annualizedYieldToMaturity < 4;
						case 3:
							return product.annualizedYieldToMaturity >= 4;
					}

					return false;
				}).indexOf(true) === -1;
			}

			if (!product.hidden && filter.annualInterestRate) {
				product.hidden = filter.annualInterestRate.map(option => {
					switch (option) {
						case 0:
							return product.annualInterestRate < 0;
						case 1:
							return product.annualInterestRate >= 0 && product.annualInterestRate <= 1;
						case 2:
							return product.annualInterestRate > 1 && product.annualInterestRate <= 2;
						case 3:
							return product.annualInterestRate > 2 && product.annualInterestRate <= 3;
						case 4:
							return product.annualInterestRate > 3 && product.annualInterestRate <= 4;
						case 5:
							return product.annualInterestRate > 4 && product.annualInterestRate <= 5;
						case 6:
							return product.annualInterestRate > 5;
					}

					return false;
				}).indexOf(true) === -1;
			}

			if (!product.hidden && filter.expiresOn) {
				product.hidden = filter.expiresOn.map(option => {
					if (!product.expiresOn) {
						return false;
					}

					const maturity = moment(product.expiresOn);

					switch (option) {
						case 0:
							return maturity < moment().add(6, 'M');
						case 1:
							return maturity >= moment().add(6, 'M') && maturity <= moment().add(12, 'M');
						case 2:
							return maturity >= moment().add(1, 'y') && maturity <= moment().add(2, 'y');
						case 3:
							return maturity >= moment().add(2, 'y') && maturity <= moment().add(3, 'y');
						case 4:
							return maturity >= moment().add(3, 'y') && maturity <= moment().add(5, 'y');
						case 5:
							return maturity > moment().add(5, 'y');
					}

					return false;
				}).indexOf(true) === -1;
			}

			if (!product.hidden && filter.capitalProtection) {
				product.hidden = filter.capitalProtection.map(option => {
					switch (option) {
						case 0:
							return !product.capitalProtection || product.capitalProtection < 0;
						case 1:
							return product.capitalProtection < 100;
						case 2:
							return product.capitalProtection >= 100;
					}

					return false;
				}).indexOf(true) === -1;
			}

			if (!product.hidden && filter.barrier) {
				product.hidden = filter.barrier.map(option => {
					switch (option) {
						case 0:
							return product.barrier < 0;
						case 1:
							return product.barrier >= 0 && product.barrier <= 10;
						case 2:
							return product.barrier > 10 && product.barrier <= 20;
						case 3:
							return product.barrier > 20 && product.barrier <= 30;
						case 4:
							return product.barrier > 30 && product.barrier <= 40;
						case 5:
							return product.barrier > 40 && product.barrier <= 50;
						case 6:
							return product.barrier > 50;
					}

					return false;
				}).indexOf(true) === -1;
			}

			this.applyKeywordFilter(product);
		});
	}

	private initHitCount(name: string, option: RadioGroupOptionInterface): void {
		// @ts-ignore
		const originalValues = this.filter[name];
		const newValues: (string | number)[] = [];

		if (newValues.indexOf(option.value) === -1) {
			newValues.push(option.value);
		}

		this.filter.set(name, newValues);
		this.applyFilters();
		option.hits = this.products.filter(product => !product.hidden).length;
		this.filter.set(name, originalValues);
		this.applyFilters();
	}

	private initFilterOptions(isPreview: boolean): void {
		InitProductFilterOptions(
			this.languageService.getCurrentLanguage(),
			isPreview,
			this.products,
			this.controls,
			this.filter);

		Object.keys(this.controls).forEach(key => {
			// @ts-ignore
			this.controls[key].options.forEach(option => {
				this.initHitCount(key, option);
			});
		});
	}

	private initGroupedProducts(useGrouping: boolean): void {
		const hasSubType = useGrouping && this.filter.subType && this.filter.subType.length > 0;
		const hasIssuer = useGrouping && this.filter.issuedBy && this.filter.issuedBy.length > 0;

		this.groupedProducts.subType = hasSubType ? this.filter.subType[0] : 0;
		this.groupedProducts.items.splice(0, this.groupedProducts.items.length);

		const pagination: IPaginationConfig = {
			totalItems: 0,
			itemsPerPage: Pagination.defaultItemsPerPage,
			pagesChunk: Pagination.defaultPagesChunk,
			configChanged: new EventEmitter()
		};

		if (!hasIssuer) {
			const products = [...this.products.filter(product => !product.hidden)];
			const pagedProducts = ProductHelper.paginateCollection(products, 0, pagination.itemsPerPage);

			pagination.totalItems = products.length;

			this.groupedProducts.items.push({
				issuedBy: null,
				pagedProducts,
				products,
				pagination
			});
		} else {
			this.filter.issuedBy.forEach(issuedBy => {
				const products = this.products.filter(product => !product.hidden && product.issuedBy === issuedBy);

				if (products.length > 0) {
					const pagedProducts = ProductHelper.paginateCollection(products, 0, pagination.itemsPerPage);

					pagination.totalItems = products.length;

					this.groupedProducts.items.push({
						issuedBy: issuedBy as string,
						pagedProducts,
						products,
						pagination
					});
				}
			});
		}
	}

	public openProductDialog(
		individual: Client,
		orderType: FundOrderTypesEnum,
		product: Product,
		cashInvestment: AccountBalanceResponseInterface,
		availableProductQuantity: number,
		component: ComponentType<any>,
		openProductDialog: any,
		isOrder: boolean,
		isMobile: boolean,
	): Observable<boolean> {
		if (orderType === FundOrderTypesEnum.All) {
			this.alertsService.addAlert('Unspecified order type.', AlertTypesEnum.Error);

			return of(false);
		}

		if (!cashInvestment && isOrder) {
			this.alertsService.addAlert('Failed to retrieve cash investments.', AlertTypesEnum.Error);

			return of(false);
		}

		if (orderType === FundOrderTypesEnum.Sell && availableProductQuantity <= 0) {
			const errorMessageKey = product.type === FundProductTypesEnum.MutualFund
				? 'PLACE_ORDER.NO_SHARES_HINT'
				: 'PLACE_ORDER.NO_UNITS_HINT';

			this.alertsService.addAlert(this.languageService.getInstantTranslationByKey(errorMessageKey), AlertTypesEnum.Error);

			return of(false);
		}

		return this.fundProductPricesService
			.getOrderPrices(product.id)
			.pipe(map(productPriceResponse => {
				if (productPriceResponse.status !== HttpStatusCodesEnum.Ok) {
					this.alertsService.addAlert('Failed to retrieve product price.', AlertTypesEnum.Error);

					return false;
				}

				const productPrices = productPriceResponse.data as FundProductOrderPricesInterface;

				const productPrice = orderType === FundOrderTypesEnum.Buy
					? productPrices?.buyPrice
					: productPrices?.sellPrice;

				if (!productPrice && isOrder) {
					this.alertsService.addAlert('Failed to retrieve product price.', AlertTypesEnum.Error);

					return false;
				}

				const panelClass = !isOrder && isMobile
					? ['', 'product-dialog-mobile']
					: [''];

				if (!isOrder) {
					panelClass.push('product-details-dialog');
				}

				this.dialog.open(component, {
					panelClass,
					data: {
						language: this.languageService.getCurrentLanguage(),
						individual,
						cashInvestment,
						productPrices,
						productPrice,
						orderType,
						product,
						availableProductQuantity,
						openProductDialog: (data: ProductDialogType) => openProductDialog
							? openProductDialog(data)
							: () => {
							}
					}
				});

				return true;
			}));
	}

	public isPreview(activatedRoute: ActivatedRoute, individual: Client): Observable<boolean> {
		if (individual) {
			return of(false);
		}

		return activatedRoute.queryParamMap.pipe(map(params => {
			return params.get('full') == null;
		}));
	}

	public removeFilter(name: string, value?: string | number): void {
		if (value) {
			// @ts-ignore
			const current: (string | number)[] = this.filter[name] ? this.filter[name] : [];
			this.filter.set(name, current.filter(item => item !== value));
		} else {
			this.filter.set(name, []);
		}
	}

	public refresh(isPreview: boolean): void {
		if (!this.controls) {
			this.initControls(isPreview);
		}
		this.applyFilters();
		this.initGroupedProducts(isPreview);
		this.initFilterOptions(isPreview);
		this.initTableColumns(isPreview);
	}

	public initControls(isPreview: boolean): void {
		this.controls = GetProductFilterControls(isPreview, this.languageService);
	}

	public getProductBySymbolCached(symbol: string): Product {
		if (!this.products) {
			return null;
		}

		return this.products.find(product => product.code === symbol);
	}

	public getProductBySymbolCachedOrFetch(symbol: string): Observable<Product> {
		if (!this.products) {
			return this.getProductBySymbol(symbol);
		}

		const product = this.products.find(product => product.code === symbol);

		if (product) {
			return of(product);
		}

		return this.getProductBySymbol(symbol);
	}

	public getProductBySymbol(symbol: string): Observable<Product> {
		return this.fundProductsService.getProductByCode(symbol).pipe(map(response => {
			if (response.status !== HttpStatusCodesEnum.Ok) {
				if (response.status !== HttpStatusCodesEnum.NotFound) {
					this.alertsService.addAlert('Failed to retrieve product.', AlertTypesEnum.Error);
				}

				return null;
			}

			return new Product(response.data as FundProductInterface );
		}))
	}

	public getProducts(): Observable<Product[]> {
		if (this.products) {
			return of(this.products);
		}

		const request = Helper.isPrototypePortal()
			? this.fundProductsService.getPrototypePublicProducts()
			: this.fundProductsService.getAlaricPublicProducts();

		return request.pipe(map(response => {
			if (response.status !== HttpStatusCodesEnum.Ok) {
				if (response.status !== HttpStatusCodesEnum.NotFound) {
					this.alertsService.addAlert('Failed to retrieve products.', AlertTypesEnum.Error);
				}

				return [];
			}

			this.products = (response.data as FundProductInterface[]).map(item => new Product(item));

			return this.products;
		}));
	}
}
